import {
  BudgetLineMatchModeApi,
  BudgetLineTypeApi,
  BudgetListPeriodTypeQuery,
  BudgetListStatusQuery,
  BudgetListTypeQuery,
  BudgetOverviewGranularity,
  BudgetPeriodTypeApi,
  BudgetScope,
  BudgetScopeApi,
  BudgetTypeApi,
} from "@/features/budgeting/model/budget-enums"
import {
  parseActiveBudgetOverviewPayload,
  parseBudgetPerformancePayload,
  parseBudgetDetail,
  parseBudgetLineTransactionsPayload,
  parseBudgetListPayload,
  parseBudgetOverviewPayload,
  unwrapBudgetEnvelope,
} from "@/features/budgeting/lib/budget-parse"

import type { BudgetPerformancePayload } from "@/features/budgeting/model/budget-performance-types"
import type {
  ActiveBudgetOverviewPayload,
  BudgetApiEnvelope,
  BudgetDetail,
  BudgetLine,
  BudgetLineTransactionsResponse,
  BudgetListResponse,
  BudgetNote,
  BudgetOverviewPayload,
  BudgetSelectedOverviewResponse,
} from "@/features/budgeting/model/budget-types"
import { http } from "@/shared/lib/api/http"

const BASE = "/finance/budgets"

export type BudgetListQuery = {
  context?: BudgetScope
  status?: BudgetListStatusQuery
  type?: BudgetListTypeQuery
  periodType?: BudgetListPeriodTypeQuery
  activityId?: string
  search?: string
  dateFrom?: string
  dateTo?: string
  includeArchived?: boolean
  branchId?: string
  page?: number
  limit?: number
  sortBy?: "updatedAt" | "createdAt" | "title" | "status" | "type" | "periodType" | "startDate" | "endDate"
  sortOrder?: "asc" | "desc"
  workspaceId?: string
}

export type BudgetOverviewQuery = {
  workspaceId: string
  dateFrom?: string
  dateTo?: string
  granularity?: BudgetOverviewGranularity
}

export type ActiveBudgetOverviewQuery = {
  workspaceId: string
  context: BudgetScope
  branchId: string
  dateFrom?: string
  dateTo?: string
  granularity?: BudgetOverviewGranularity
}

export type BudgetPerformanceQuery = {
  workspaceId: string
  context: BudgetScope
  branchId: string
  dateFrom?: string
  dateTo?: string
}

export type BudgetLineTransactionsQuery = {
  workspaceId: string
  lineId?: string
  dateFrom?: string
  dateTo?: string
  page?: number
  limit?: number
}

function buildListParams(q: BudgetListQuery): Record<string, string | number | boolean | undefined> {
  return {
    context: q.context,
    status: q.status,
    type: q.type,
    periodType: q.periodType,
    activityId: q.activityId,
    search: q.search,
    dateFrom: q.dateFrom,
    dateTo: q.dateTo,
    includeArchived: q.includeArchived,
    branchId: q.branchId,
    page: q.page,
    limit: q.limit,
    sortBy: q.sortBy,
    sortOrder: q.sortOrder,
    workspaceId: q.workspaceId,
  }
}

export async function fetchBudgetList(query: BudgetListQuery = {}): Promise<BudgetListResponse> {
  const response = await http.get<BudgetApiEnvelope<BudgetListResponse>>(BASE, { params: buildListParams(query) })
  return parseBudgetListPayload(unwrapBudgetEnvelope(response.data))
}

export async function fetchBudgetDetail(id: string, workspaceId: string): Promise<BudgetDetail> {
  const response = await http.get<BudgetApiEnvelope<BudgetDetail>>(`${BASE}/${encodeURIComponent(id)}`, {
    params: { workspaceId },
  })
  return parseBudgetDetail(unwrapBudgetEnvelope(response.data))
}

export async function fetchBudgetOverview(id: string, query: BudgetOverviewQuery): Promise<BudgetOverviewPayload> {
  const response = await http.get<BudgetApiEnvelope<BudgetSelectedOverviewResponse>>(`${BASE}/${encodeURIComponent(id)}/overview`, {
    params: query,
  })
  return parseBudgetOverviewPayload(unwrapBudgetEnvelope(response.data))
}

/**
 * The aggregate carries its own `currency` now (IBA-114/116), so the caller no longer passes one in. The old
 * `currencyCode?` parameter defaulted to `"USD"` deep inside the parser — which is how a branch that keeps its
 * books in Congolese francs got a dollar sign on its budget dashboard.
 */
export async function fetchActiveBudgetOverview(
  query: ActiveBudgetOverviewQuery,
): Promise<BudgetOverviewPayload> {
  const response = await http.get<BudgetApiEnvelope<ActiveBudgetOverviewPayload>>(`${BASE}/active-overview`, {
    params: query,
  })
  return parseActiveBudgetOverviewPayload(unwrapBudgetEnvelope(response.data))
}

/**
 * IBA-157 — planned vs realised by income account, expense account and fund.
 *
 * Deliberately its own endpoint (ADR-0011 keeps summary and performance apart), so the Budget Health tile
 * does not fetch a whole account x fund matrix to draw one bar.
 */
export async function fetchBudgetPerformance(
  query: BudgetPerformanceQuery,
): Promise<BudgetPerformancePayload> {
  const response = await http.get<BudgetApiEnvelope<unknown>>(`${BASE}/performance`, { params: query })
  return parseBudgetPerformancePayload(unwrapBudgetEnvelope(response.data))
}

export async function fetchBudgetLineTransactions(
  budgetId: string,
  lineId: string,
  query: BudgetLineTransactionsQuery,
): Promise<BudgetLineTransactionsResponse> {
  const response = await http.get<BudgetApiEnvelope<BudgetLineTransactionsResponse>>(
    `${BASE}/${encodeURIComponent(budgetId)}/lines/${encodeURIComponent(lineId)}/transactions`,
    { params: query },
  )
  return parseBudgetLineTransactionsPayload(unwrapBudgetEnvelope(response.data))
}

export async function fetchBudgetTransactions(
  budgetId: string,
  query: BudgetLineTransactionsQuery,
): Promise<BudgetLineTransactionsResponse> {
  const response = await http.get<BudgetApiEnvelope<BudgetLineTransactionsResponse>>(
    `${BASE}/${encodeURIComponent(budgetId)}/transactions`,
    { params: query },
  )
  return parseBudgetLineTransactionsPayload(unwrapBudgetEnvelope(response.data))
}

export type CreateBudgetLineInput = {
  type: BudgetLineTypeApi
  matchMode?: BudgetLineMatchModeApi
  title: string
  /**
   * A decimal money string (IBA-116) — `"1500"`, `"1500.5"`, `"1500.00"`. Never a JSON number.
   *
   * Deliberately NOT a `MoneyString`: that brand means *canonical for its currency*, and canonicalizing here
   * would mean rounding what the user typed before they were told it was wrong. `"1500.75"` in RWF is not a
   * slightly-too-precise franc, it is not a franc — and it is the SERVER that says so, with a field error, so
   * the user hears about it instead of watching their amount silently become 1501.
   */
  plannedAmount: string
  incomeAccountId?: string | null
  expenseAccountId?: string | null
  /** Optional fund the line targets — omit/null for any fund (ADR-0021 money purpose). */
  fundId?: string | null
  notes?: string | null
}

export type CreateBudgetInput = {
  scope: BudgetScopeApi
  branchId?: string | null
  type: BudgetTypeApi
  activityId?: string | null
  title: string
  description?: string | null
  periodType: BudgetPeriodTypeApi
  startDate: string
  endDate: string
  currency: string
  /**
   * Optional fund allow-list constraining which funds this budget's LINES may plan against (IBA-157).
   * Omitted or empty = unconstrained. A planning guard only; it never affects balances or actuals.
   */
  allowedFundIds?: string[]
  lines?: CreateBudgetLineInput[]
  workspaceId: string
}

export type PatchBudgetInput = {
  workspaceId: string
  branchId?: string | null
  type?: BudgetTypeApi
  activityId?: string | null
  title?: string
  description?: string | null
  periodType?: BudgetPeriodTypeApi
  startDate?: string
  endDate?: string
  currency?: string
  /** Replaces the whole allow-list when present (`[]` clears it); omitting it leaves the list untouched. */
  allowedFundIds?: string[]
}

export async function createBudget(payload: CreateBudgetInput): Promise<BudgetDetail> {
  const response = await http.post<BudgetApiEnvelope<BudgetDetail>>(BASE, payload)
  return parseBudgetDetail(unwrapBudgetEnvelope(response.data))
}

export async function patchBudget(id: string, payload: PatchBudgetInput): Promise<BudgetDetail> {
  const response = await http.patch<BudgetApiEnvelope<BudgetDetail>>(`${BASE}/${encodeURIComponent(id)}`, payload)
  return parseBudgetDetail(unwrapBudgetEnvelope(response.data))
}

export async function addBudgetLine(
  budgetId: string,
  payload: { workspaceId: string; line: CreateBudgetLineInput },
): Promise<BudgetLine> {
  const response = await http.post<BudgetApiEnvelope<BudgetLine>>(`${BASE}/${encodeURIComponent(budgetId)}/lines`, payload)
  return unwrapBudgetEnvelope(response.data)
}

export async function patchBudgetLine(
  budgetId: string,
  lineId: string,
  payload: { workspaceId: string } & Partial<CreateBudgetLineInput>,
): Promise<BudgetLine> {
  const response = await http.patch<BudgetApiEnvelope<BudgetLine>>(
    `${BASE}/${encodeURIComponent(budgetId)}/lines/${encodeURIComponent(lineId)}`,
    payload,
  )
  return unwrapBudgetEnvelope(response.data)
}

export async function deleteBudgetLine(budgetId: string, lineId: string, workspaceId: string): Promise<void> {
  await http.delete<BudgetApiEnvelope<{ deleted: true }>>(
    `${BASE}/${encodeURIComponent(budgetId)}/lines/${encodeURIComponent(lineId)}`,
    { params: { workspaceId } },
  )
}

/** Lifecycle transitions that carry no note use a querystring workspaceId; those that take a note
 * (approve / request-changes / revise) send a JSON body. */
async function postBudgetTransition(id: string, action: string, workspaceId: string): Promise<BudgetDetail> {
  const response = await http.post<BudgetApiEnvelope<BudgetDetail>>(
    `${BASE}/${encodeURIComponent(id)}/${action}`,
    {},
    { params: { workspaceId } },
  )
  return parseBudgetDetail(unwrapBudgetEnvelope(response.data))
}

async function postBudgetTransitionWithComment(
  id: string,
  action: string,
  payload: { workspaceId: string; comment?: string },
): Promise<BudgetDetail> {
  const response = await http.post<BudgetApiEnvelope<BudgetDetail>>(`${BASE}/${encodeURIComponent(id)}/${action}`, payload)
  return parseBudgetDetail(unwrapBudgetEnvelope(response.data))
}

export const submitBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "submit", workspaceId)
export const resubmitBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "resubmit", workspaceId)
export const reviewBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "review", workspaceId)
export const returnToDraftBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "back-to-draft", workspaceId)
export const markReadyBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "mark-ready", workspaceId)

export const approveBudget = (id: string, payload: { workspaceId: string; comment?: string }) =>
  postBudgetTransitionWithComment(id, "approve", payload)
export const requestChangesBudget = (id: string, payload: { workspaceId: string; comment?: string }) =>
  postBudgetTransitionWithComment(id, "request-changes", payload)
export const reviseBudget = (id: string, payload: { workspaceId: string; comment?: string }) =>
  postBudgetTransitionWithComment(id, "revise", payload)

export type BudgetNotesPage = { items: BudgetNote[]; nextCursor: string | null }

export async function listBudgetNotes(
  id: string,
  params: { workspaceId: string; limit?: number; cursor?: string },
): Promise<BudgetNotesPage> {
  const response = await http.get<BudgetApiEnvelope<{ items?: BudgetNote[]; nextCursor?: string | null }>>(
    `${BASE}/${encodeURIComponent(id)}/notes`,
    {
      params: {
        workspaceId: params.workspaceId,
        ...(params.limit ? { limit: params.limit } : {}),
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    },
  )
  const data = unwrapBudgetEnvelope(response.data)
  return { items: data?.items ?? [], nextCursor: data?.nextCursor ?? null }
}

export async function addBudgetNote(id: string, payload: { workspaceId: string; body: string }): Promise<BudgetNote> {
  const response = await http.post<BudgetApiEnvelope<BudgetNote>>(`${BASE}/${encodeURIComponent(id)}/notes`, payload)
  return unwrapBudgetEnvelope(response.data)
}

export async function updateBudgetNote(
  id: string,
  noteId: string,
  payload: { workspaceId: string; body: string },
): Promise<BudgetNote> {
  const response = await http.patch<BudgetApiEnvelope<BudgetNote>>(
    `${BASE}/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`,
    payload,
  )
  return unwrapBudgetEnvelope(response.data)
}

export async function deleteBudgetNote(id: string, noteId: string, workspaceId: string): Promise<void> {
  await http.delete(`${BASE}/${encodeURIComponent(id)}/notes/${encodeURIComponent(noteId)}`, { params: { workspaceId } })
}

export const closeBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "close", workspaceId)
export const archiveBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "archive", workspaceId)
export const restoreBudget = (id: string, workspaceId: string) => postBudgetTransition(id, "restore", workspaceId)

export type BudgetHistoryEvent = {
  id: string
  actionType: string
  messageKey: string
  summary: string | null
  createdAt: string
  actorUserId: string | null
  actorName: string | null
  actorAvatarUrl: string | null
}

export async function fetchBudgetHistory(id: string, workspaceId: string): Promise<BudgetHistoryEvent[]> {
  const response = await http.get<BudgetApiEnvelope<{ items?: BudgetHistoryEvent[] }>>(
    `${BASE}/${encodeURIComponent(id)}/history`,
    { params: { workspaceId } },
  )
  return unwrapBudgetEnvelope(response.data)?.items ?? []
}

export type BudgetVersion = {
  id: string
  versionNumber: number
  status: string
  reason: string | null
  isCurrent: boolean
  createdAt: string
  createdByName: string | null
  createdByAvatarUrl: string | null
  approvedAt: string | null
  approvedByName: string | null
  approvedByAvatarUrl: string | null
}

export async function fetchBudgetVersions(id: string, workspaceId: string): Promise<BudgetVersion[]> {
  const response = await http.get<BudgetApiEnvelope<{ items?: BudgetVersion[] }>>(
    `${BASE}/${encodeURIComponent(id)}/versions`,
    { params: { workspaceId } },
  )
  return unwrapBudgetEnvelope(response.data)?.items ?? []
}
